import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { commitLocalUpdate, graphql } from "react-relay";

import { faSmile, faSmilePlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy, { type TippyProps, useSingleton } from "@tippyjs/react";
import cx from "classnames";
import { getUnixTime } from "date-fns";
import EventEmitter from "events";
import { cloneDeep, isEmpty, isEqual } from "lodash";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { roundArrow, type Instance as TippyInstance } from "tippy.js";
import "tippy.js/dist/svg-arrow.css";
import "tippy.js/dist/tippy.css";

import { Emoji, Picker } from "~/common/Emoji";
import { useCurrentFullscreenElement } from "~/common/Fullscreen";
import ViewportContext from "~/common/ViewportContext";
import { Extension, type MenuEntry, type MenuEntryComponentProps } from "~/lib/prose/extension";
import { commitMutationPromise } from "~/lib/relayUtil";
import { isMobile } from "~/lib/utils";
import { environment } from "~/models";
import toast from "~/ui/toast";

import { addAnnotation, REACTION_ANNOTATION_ORDER, removeAnnotation } from "./blockAnnotation";
import { getBlockBoundaryState } from "./blockBoundary";
import type { EmojiSearchResult } from "./emoji";

import type { reactionsAddReactionMutation } from "~/__generated__/reactionsAddReactionMutation.graphql";
import type { reactionsRemoveReactionMutation } from "~/__generated__/reactionsRemoveReactionMutation.graphql";

import styles from "./reactions.module.css";

export const ReactionsPluginKey = new PluginKey<ReactionsState>("block-reactions");

/** Our storage format for raw reaction data */
type ReactionsData = {
	[block: string]: {
		[emojiKey: string]: {
			emojiName: string;
			timestamp: number;
			reactors: {
				userId: string;
				userFirstName: string; // email or first name
				timestamp: number;
			}[];
		};
	};
};

type ReactionData = {
	id: string;
	timestamp: number;
	userId: string;
	userFirstName: string;
	emojiName: string;
	emojiUuid: string | null;
	blockKey: string;
};

type InitialReactionsData = ReactionData[];

/** The format of data coming from the websocket handler, describing the addition or deletion of a reaction */
export type ReactionUpdateData = ReactionData & {
	operation: "add" | "remove";
};

function onMouseDown() {
	if (document.activeElement) activeElementWhenClickBegan = document.activeElement;
}

// Need forwardRef to use as Tippy reference.
const Reaction = React.forwardRef<
	HTMLButtonElement,
	{
		blockKey: string;
		emojiName: string;
		numReactions: number;
		userDidReact: boolean;
		ext: Reactions;
		accountId: string;
	}
>(function Reaction({ ext, blockKey, emojiName, numReactions, userDidReact, accountId }, ref) {
	const onClick = useCallback(() => {
		if (!userDidReact) {
			ext.addReaction(blockKey, emojiName);
		} else {
			ext.removeReaction(blockKey, emojiName);
		}
	}, [userDidReact, blockKey, emojiName, ext]);

	return (
		<button
			ref={ref}
			onClick={onClick}
			onMouseDown={onMouseDown}
			className={cx(styles.reactionButton, { [styles.active]: userDidReact })}
		>
			<Emoji
				size={14}
				emoji={emojiName}
				native
				accountId={accountId}
				fallbackUUID={ext.emojiNameToFallbackUUID[emojiName]}
			/>{" "}
			{numReactions}
		</button>
	);
});

function renderEmojiPickerTippy() {
	return <div />;
}

const popperOptions: TippyProps["popperOptions"] = {
	modifiers: [
		{
			name: "preventOverflow",
			options: {
				altAxis: true,
			},
		},
		{
			name: "flip",
			options: {
				fallbackPlacements: ["top", "bottom", "left", "right"],
			},
		},
	],
};

function EmojiPickerTippy({
	ext,
	blockKey,
	children,
}: {
	ext: Reactions;
	blockKey: string;
	children: React.ReactElement;
}) {
	const onShow = useCallback(
		(instance: TippyInstance) => {
			if (!ext) return;
			instance.popper.appendChild(ext.picker.get(blockKey, instance.hide.bind(instance)));
		},
		[ext, blockKey],
	);

	const onDestroy = useCallback(
		(_instance: TippyInstance) => {
			if (!ext) return;
			ReactDOM.unmountComponentAtNode(ext.picker.node);
		},
		[ext],
	);

	const currentFullscreenElement = useCurrentFullscreenElement();

	return (
		<Tippy
			trigger="click"
			animation={false}
			interactive
			render={renderEmojiPickerTippy}
			onShow={onShow}
			onHide={onDestroy}
			onDestroy={onDestroy}
			appendTo={currentFullscreenElement ?? document.body}
			popperOptions={popperOptions}
		>
			{children}
		</Tippy>
	);
}

function AddReactionButton({ blockKey, ext }: { blockKey: string; ext: Reactions }) {
	const { isIPhoneSE } = useContext(ViewportContext);
	if (isIPhoneSE || isMobile) return null;

	return (
		<EmojiPickerTippy blockKey={blockKey} ext={ext} key={blockKey}>
			<button className={styles.addReactionButton}>
				<FontAwesomeIcon icon={faSmilePlus} />
			</button>
		</EmojiPickerTippy>
	);
}

// can't use `as const` because tippy's types accept a mutable array :thunking:
const tippyDelay: [number, number] = [500, 0];

function ReactionsWidget({
	reactions: initialReactions,
	accountId,
	userId,
	blockKey,
	ext,
	reactionsEmitter,
}: {
	reactions: ReactionsData[string];
	userId: string;
	accountId: string;
	blockKey: string;
	ext: Reactions;
	reactionsEmitter?: EventEmitter;
}) {
	const [source, target] = useSingleton();
	const [reactions, setReactions] = useState(initialReactions);
	useEffect(() => {
		const listener = (data: null | ReactionsData[string]) => {
			setReactions(existingData => {
				if (isEqual(existingData, data)) {
					return existingData;
				}
				return data || {};
			});
		};
		reactionsEmitter?.addListener("data", listener);
		return () => {
			reactionsEmitter?.removeListener("data", listener);
		};
	}, [setReactions, reactionsEmitter]);

	const sortedReactions = useMemo(() => {
		return Object.entries(reactions)
			.sort(([, { timestamp: a }], [, { timestamp: b }]) => a - b)
			.map(([emojiKey, { emojiName, reactors }]) => ({
				emojiKey,
				emojiName,
				reactors: reactors.sort((a, b) => a.timestamp - b.timestamp),
			}));
	}, [reactions]);

	const reactionsCount = Object.keys(reactions).length;

	return (
		<>
			<Tippy
				singleton={source}
				delay={tippyDelay}
				theme="center"
				arrow={roundArrow}
				maxWidth={220}
				className={styles.reactorsTippy}
			/>

			{sortedReactions.map(({ emojiKey, emojiName, reactors }) => (
				<Tippy
					key={emojiKey}
					singleton={target}
					content={
						<>
							<div className={styles.reactorMessageEmojiWrapper} contentEditable={false}>
								<div className={styles.reactorMessageEmoji}>
									<Emoji emoji={emojiName} size={24} native accountId={accountId} />
								</div>
							</div>
							<p className={styles.reactorMessage}>
								{/* eslint-disable-next-line max-len */}
								<b className={styles.reactorMessageReactors}>
									{nameList(reactors.map(user => user.userFirstName))}
								</b>{" "}
								reacted with {emojiName}
							</p>
						</>
					}
				>
					<Reaction
						blockKey={blockKey}
						emojiName={emojiName}
						numReactions={reactors.length}
						userDidReact={reactors.some(u => u.userId === userId)}
						accountId={accountId}
						ext={ext}
					/>
				</Tippy>
			))}

			{0 < reactionsCount && reactionsCount < 10 && <AddReactionButton blockKey={blockKey} ext={ext} />}
		</>
	);
}

class AddReactionPicker {
	didSelect = false;
	blockKey?: string;
	hideTippy?: () => void;
	readonly ext: Reactions;
	readonly node: Element;

	constructor(ext: Reactions) {
		this.ext = ext;
		this.node = document.createElement("div");
		this.node.setAttribute("data-ignore-focus-lock", "true");
	}

	// Using this cache here would cause the tab switching inside the emojimark to not work.
	// Possibly due to some edge case from EmojiMart not being able to handle scroll height when it's unmounted?
	// lazily render the emoji picker into this.node
	// private didRenderPicker = false;
	get(blockKey: string, hideTippy: () => void) {
		this.hideTippy = hideTippy;
		this.blockKey = blockKey;
		// if (this.didRenderPicker) return this.node;
		// this.didRenderPicker = true;
		ReactDOM.render(
			<Picker
				key={this.blockKey}
				accountId={this.ext.account.id}
				autoFocus
				emojiTooltip
				native
				emoji=""
				darkMode={document.documentElement.dataset.theme === "theme-dark"}
				perLine={7}
				style={{
					boxShadow:
						"0px 9px 20px rgba(35, 36, 37, 0.1), 0px 1px 4px rgba(0, 0, 0, 0.05), 0px -1px 2px rgba(0, 0, 0, 0.03),\n" +
						"\t\t\t0px 9px 20px rgba(35, 36, 37, 0.1)",
				}}
				// This doesn't work; holding shift while clicking an emoji still
				// closes the picker since the button it self is destroyed and
				// created again I guess?
				// eslint-disable-next-line react/jsx-no-bind
				onClick={(emoji: EmojiSearchResult, event) => {
					this.didSelect = true;
					const ident = emoji.colons ?? emoji.id;
					if (!ident) {
						console.warn("Emoji has no .colons nor .id... what is it?", emoji);
						this.hideTippy?.();
						return;
					}
					this.blockKey && this.ext.addReaction(this.blockKey, ident);
					if (!event.shiftKey) this.hideTippy?.();
				}}
				// eslint-disable-next-line react/jsx-no-bind
				onSelect={(emoji: EmojiSearchResult) => {
					this.didSelect = true;
					const ident = emoji.colons ?? emoji.id;
					if (!ident) {
						console.warn("Emoji has no .colons nor .id... what is it?", emoji);
						this.hideTippy?.();
						return;
					}
					this.blockKey && this.ext.addReaction(this.blockKey, ident);
					this.hideTippy?.();
				}}
				skinEmoji="+1"
				title=""
			/>,
			this.node,
		);
		return this.node;
	}
}

let activeElementWhenClickBegan: Element | null = null;

function nameList(names: readonly string[], nameLimit = 20) {
	if (names.length === 1) {
		return names[0];
	} else if (names.length <= nameLimit + 1) {
		return `${names.slice(0, names.length - 1).join(", ")}${names.length === 2 ? "" : ","} and ${
			names[names.length - 1]
		}`;
	} else {
		return `${names.slice(0, nameLimit).join(", ")}, and ${names.length - nameLimit} others`;
	}
}

interface ReactionsState {
	/** Just the data about who reacted to what blocks with what emoji */
	reactionsData: ReactionsData;
	/** The widgets displaying the emoji and reaction buttons */
	decorations: DecorationSet;
	/** Position of end of blocks by key */
	blockBoundaries: { [k: string]: [number, number, ProseNode] };
}

/**
 * Non-blocking lock (i.e. you either get it or ya don't; no waiting). Makes
 * sure you can't try to add the same emoji more than once while the request is
 * still in flight.
 */
class BlockEmojiLock {
	// map of block keys to sets of emoji being added
	private inFlight = new Map<string, Set<string>>();

	acquire(blockKey: string, emojiKey: string): boolean {
		if (this.inFlight.get(blockKey)?.has(emojiKey)) {
			return false;
		} else {
			let forBlock = this.inFlight.get(blockKey);
			if (!forBlock) {
				forBlock = new Set();
				this.inFlight.set(blockKey, forBlock);
			}
			forBlock.add(emojiKey);
			return true;
		}
	}

	release(blockKey: string, emojiKey: string) {
		const forBlock = this.inFlight.get(blockKey);
		if (!forBlock?.has(emojiKey)) {
			throw new Error("Invalid state");
		}
		forBlock.delete(emojiKey);
		if (!forBlock.size) {
			this.inFlight.delete(blockKey);
		}
	}
}

function ReactionsMenuEntryComponent(props: MenuEntryComponentProps) {
	const { editorView, extension: menuExtension } = props;
	const reactionsExtension = menuExtension.editor?.extensions.find((ext): ext is Reactions => ext instanceof Reactions);
	const selectedBlock = useRef();

	const { from, to } = editorView.state.selection;
	editorView.state.doc.nodesBetween(from, to, node => {
		const blockId = node.attrs.blockId;
		if (blockId) {
			selectedBlock.current = blockId;
		}
	});

	if (!reactionsExtension) throw new Error("Can't render reactions menu without reactions extension");

	return (
		<EmojiPickerTippy ext={reactionsExtension} blockKey={selectedBlock.current || ""}>
			<div>{props.children}</div>
		</EmojiPickerTippy>
	);
}

export default class Reactions extends Extension {
	name = "block-reactions";
	noteId: string;
	user: { id: string; firstName: string };
	account: { id: string };
	initialData: ReactionsData;
	picker = new AddReactionPicker(this);
	// Map of block key to reactions widget dom node
	reactionWidgetNodes = new Map<string, Element>();
	blockDataEmitters = new Map<string, EventEmitter>();

	emojiNameToFallbackUUID: { [key: string]: string } = {};

	constructor({
		user,
		noteId,
		account,
		initialData,
	}: {
		user: { id: string; firstName: string };
		noteId: string;
		account: { id: string };
		initialData: InitialReactionsData;
	}) {
		super();

		this.noteId = noteId;
		this.user = user;
		this.account = account;
		this.initialData = {};

		for (const { userId, userFirstName, timestamp, emojiName, blockKey, emojiUuid } of initialData) {
			if (!this.initialData[blockKey]) this.initialData[blockKey] = {};
			const blockData = this.initialData[blockKey];
			const emojiKey = emojiName;
			if (emojiUuid) {
				this.emojiNameToFallbackUUID[emojiName] = emojiUuid;
			}
			if (!blockData[emojiKey]) blockData[emojiKey] = { emojiName, timestamp, reactors: [] };
			blockData[emojiKey].reactors.push({ userId, userFirstName, timestamp });
		}
	}

	menuEntry(): MenuEntry {
		return {
			icon: faSmile,
			title: "Reaction",
			run: () => () => true,
			component: ReactionsMenuEntryComponent,
			section: "reaction" as const,
			priority: 0,
		};
	}

	removeAtKey = (key: string) => {
		let dom = this.reactionWidgetNodes.get(key);
		if (dom) {
			if (this.editorView) removeAnnotation(this.editorView, key, dom);
			ReactDOM.unmountComponentAtNode(dom);
		}
		this.reactionWidgetNodes.delete(key);
		this.blockDataEmitters.delete(key);
	};

	applyUpdateData = (oldData: ReactionsData, update: ReactionUpdateData): ReactionsData => {
		const { operation, userId, userFirstName, timestamp, emojiName, blockKey, emojiUuid } = update;
		const newData = cloneDeep(oldData);

		const emojiKey = emojiName;
		if (operation === "add") {
			if (emojiUuid) {
				this.emojiNameToFallbackUUID[emojiName] = emojiUuid;
			}
			if (!newData[blockKey]) newData[blockKey] = {};
			const blockReactions = newData[blockKey];
			if (!blockReactions[emojiKey]) blockReactions[emojiKey] = { emojiName, timestamp, reactors: [] };
			if (!blockReactions[emojiKey].reactors.some(u => u.userId === userId)) {
				blockReactions[emojiKey].reactors.push({ userId, userFirstName, timestamp });
			}
		} else if (operation === "remove") {
			const blockReactions = newData[blockKey];
			if (blockReactions?.[emojiKey]) {
				const reactors = blockReactions[emojiKey].reactors;

				blockReactions[emojiKey].reactors = reactors.filter(u => u.userId !== userId);
				if (blockReactions[emojiKey].reactors.length === 0) {
					delete blockReactions[emojiKey];
				}
			}

			if (!blockReactions || Object.keys(blockReactions).length === 0) {
				delete newData[blockKey];
			}
		} else {
			console.warn("Unknown reaction update operation", operation);
		}

		const newBlockReaction = newData[blockKey];
		this.blockDataEmitters.get(blockKey)?.emit("data", newBlockReaction);
		if (isEmpty(newBlockReaction)) {
			this.removeAtKey(blockKey);
		}

		return newData;
	};

	refreshReactionsData = (dataFromQuery: InitialReactionsData) => {
		const view = this.editorView;
		if (!view) return;
		const newState: ReactionsData = {};

		for (const { userId, userFirstName, emojiName, timestamp, emojiUuid, blockKey } of dataFromQuery) {
			if (!newState[blockKey]) newState[blockKey] = {};
			const blockData = newState[blockKey];
			const emojiKey = emojiName;
			if (emojiUuid) {
				this.emojiNameToFallbackUUID[emojiName] = emojiUuid;
			}
			if (!blockData[emojiKey]) blockData[emojiKey] = { emojiName, timestamp, reactors: [] };
			blockData[emojiKey].reactors.push({ userId, userFirstName, timestamp });
		}

		view.dispatch(view.state.tr.setMeta("reactionsRefetched", newState));

		this.blockDataEmitters.forEach((emitter, k) => {
			const data = newState[k];
			if (data) {
				// TODO This can happen even before any listeners are added. Figure out why.
				// If we can prevent this, we can delete the manual commit to the relay store.
				emitter.emit("data", data);
			}
		});
	};

	updateWidgets = (newState: EditorState, currentPluginState: ReactionsState, reactionsDataChanged = false) => {
		const editorView = this.editorView;
		if (!editorView) return;
		// this has the side effect of modifying currentPluginState
		const blockBoundaryState = getBlockBoundaryState(newState);
		if (!blockBoundaryState) return;
		const { blockBoundaries: newBlockBoundaries } = blockBoundaryState;
		const removedBlockKeys = new Set(
			Object.keys(currentPluginState.blockBoundaries).filter(k => !(k in newBlockBoundaries)),
		);
		const blockKeysWithReactionsRemoved = new Set(
			[...this.reactionWidgetNodes.keys()].filter(k => !(k in currentPluginState.reactionsData)),
		);

		removedBlockKeys.forEach(key => {
			this.removeAtKey(key);
			// To prevent UI inconsistencies with undo operations (cmd+z),
			// we need to remove the reactions from the plugin state
			// Note: if user deletes a block, the undo operation does not restore the reactions
			delete currentPluginState.reactionsData[key];
		});

		// We also need to delete the reactions from relay local store
		// To prevent UI issues in case the user clicks add/remove after an undo operation
		if (removedBlockKeys.size > 0) {
			setTimeout(() => {
				commitLocalUpdate(environment, store => {
					const note = store.get(this.noteId);
					if (!note) return;

					const existingReactionsJSON = note.getValue("reactions");
					if (!existingReactionsJSON) return;
					const existingReactions = JSON.parse(existingReactionsJSON as string) as InitialReactionsData;

					const newReactions = existingReactions.filter(item => !removedBlockKeys.has(item.blockKey));
					note.setValue(JSON.stringify(newReactions), "reactions");
				});
			}, 0);
		}
		blockKeysWithReactionsRemoved.forEach(this.removeAtKey);

		const decos: Decoration[] = [];
		if (!reactionsDataChanged && isEqual(newBlockBoundaries, currentPluginState.blockBoundaries)) return;

		Object.entries(currentPluginState.reactionsData).forEach(([k, v]) => {
			if (!(k in newBlockBoundaries)) return;
			let dom = this.reactionWidgetNodes.get(k);
			if (!dom) {
				const div = document.createElement("div");
				div.contentEditable = "false";
				dom = div;
				dom.classList.add(styles.reactionsWidget);
				this.reactionWidgetNodes.set(k, dom);
				let emitter = this.blockDataEmitters.get(k);
				if (!emitter) {
					emitter = new EventEmitter();
					this.blockDataEmitters.set(k, emitter);
				}
				ReactDOM.render(
					<ReactionsWidget
						reactions={v}
						userId={this.user.id}
						blockKey={k}
						ext={this}
						reactionsEmitter={emitter}
						accountId={this.account.id}
					/>,
					dom,
				);
				addAnnotation(editorView, k, dom, REACTION_ANNOTATION_ORDER);
			}
		});

		currentPluginState.blockBoundaries = newBlockBoundaries;
		currentPluginState.decorations = DecorationSet.create(newState.doc, decos);
	};

	get plugins() {
		const ext = this;
		return [
			new Plugin<ReactionsState>({
				key: ReactionsPluginKey,
				props: {
					decorations(state) {
						return ReactionsPluginKey.getState(state)?.decorations;
					},
					handleClick: (_view, _pos, event) => {
						if (
							event.target instanceof HTMLElement &&
							event.target.classList.contains(styles.reactionButton) &&
							activeElementWhenClickBegan instanceof HTMLElement
						) {
							activeElementWhenClickBegan.focus();
						}
						return false;
					},
				},
				state: {
					init(_config, state: EditorState) {
						const value = {
							reactionsData: ext.initialData,
							decorations: DecorationSet.create(state.doc, []),
							blockBoundaries: {},
						};
						ext.updateWidgets(state, value);
						return value;
					},
					apply(tr, value, _oldState, newState) {
						let reactionsDataChanged = false;

						if (tr.getMeta(ReactionsPluginKey)) {
							const newData: ReactionUpdateData = tr.getMeta(ReactionsPluginKey);
							// this has the side effect of notifying react nodes
							reactionsDataChanged = true;
							value.reactionsData = ext.applyUpdateData(value.reactionsData, newData);
						} else if (tr.getMeta("reactionsRefetched")) {
							reactionsDataChanged = true;
							value.reactionsData = tr.getMeta("reactionsRefetched");
						}

						ext.updateWidgets(newState, value, reactionsDataChanged);
						return value;
					},
				},
			}),
		];
	}

	dispatchReactionUpdate(updateData: ReactionUpdateData, persist = false) {
		if (persist) {
			commitLocalUpdate(environment, store => {
				const note = store.get(this.noteId);
				if (!note) return;

				const existingReactionsJSON = note.getValue("reactions");
				if (!existingReactionsJSON) return;
				const existingReactions = JSON.parse(existingReactionsJSON as string) as InitialReactionsData;

				if (updateData.operation === "add") {
					if (existingReactions.some(r => r.id === updateData.id)) return;

					const newReactions = existingReactions.concat([
						{
							id: updateData.id,
							timestamp: updateData.timestamp,
							userId: updateData.userId,
							userFirstName: updateData.userFirstName,
							emojiName: updateData.emojiName,
							blockKey: updateData.blockKey,
							emojiUuid: null,
						},
					]);
					note.setValue(JSON.stringify(newReactions), "reactions");
				} else if (updateData.operation === "remove") {
					const newReactions = existingReactions.filter(item => item.id !== updateData.id);
					note.setValue(JSON.stringify(newReactions), "reactions");
				}
			});
		}
		this.editorView?.dispatch(this.editorView.state.tr.setMeta(ReactionsPluginKey, updateData));
	}

	private addLock = new BlockEmojiLock();
	private removeLock = new BlockEmojiLock();

	async addReaction(blockKey: string, emojiName: string) {
		const { noteId } = this;
		const emojiKey = emojiName;

		if (!this.addLock.acquire(blockKey, emojiKey)) {
			return;
		}

		if (this.editorView) {
			const reactions = ReactionsPluginKey.getState(this.editorView.state)?.reactionsData;
			if (reactions) {
				const blockReactions = reactions[blockKey];
				if (blockReactions) {
					const blockReactionsList = Object.keys(blockReactions);
					if (!blockReactionsList.includes(emojiKey) && blockReactionsList.length >= 10) {
						this.addLock.release(blockKey, emojiKey);
						return;
					}
				}
			}
		}

		// optimistic update
		this.dispatchReactionUpdate({
			operation: "add",
			timestamp: getUnixTime(new Date()),
			blockKey,
			emojiName,
			userId: this.user.id,
			userFirstName: this.user.firstName,
			emojiUuid: null,
			id: "",
		});

		const resp = await commitMutationPromise<reactionsAddReactionMutation>(environment, {
			mutation: graphql`
				mutation reactionsAddReactionMutation($noteId: ID!, $blockKey: String!, $emojiName: String!) {
					NoteBlock_AddReaction(noteId: $noteId, blockKey: $blockKey, emojiName: $emojiName) {
						newReaction
					}
				}
			`,
			variables: { noteId, blockKey, emojiName },
		});

		if (!resp.NoteBlock_AddReaction) {
			toast.error("Failed to add reaction", { title: "Error" });

			// undo optimistic update
			this.dispatchReactionUpdate({
				operation: "remove",
				timestamp: getUnixTime(new Date()),
				emojiName,
				blockKey,
				userId: this.user.id,
				userFirstName: this.user.firstName,
				emojiUuid: null,
				id: "",
			});
		}

		this.addLock.release(blockKey, emojiKey);
	}

	async removeReaction(blockKey: string, emojiName: string) {
		const { noteId } = this;
		const emojiKey = emojiName;
		if (!this.removeLock.acquire(blockKey, emojiKey)) {
			return;
		}

		// optimistic update
		this.dispatchReactionUpdate({
			operation: "remove",
			timestamp: getUnixTime(new Date()),
			blockKey,
			emojiName,
			userId: this.user.id,
			userFirstName: this.user.firstName,
			emojiUuid: null,
			id: "",
		});

		const resp = await commitMutationPromise<reactionsRemoveReactionMutation>(environment, {
			mutation: graphql`
				mutation reactionsRemoveReactionMutation($noteId: ID!, $blockKey: String!, $emojiName: String!) {
					NoteBlock_RemoveReaction(noteId: $noteId, blockKey: $blockKey, emojiName: $emojiName) {
						deletedId
						user {
							id
							firstName
						}
					}
				}
			`,
			variables: { noteId, blockKey, emojiName },
		});

		if (!resp.NoteBlock_RemoveReaction) {
			toast.error("Failed to remove reaction", { title: "Error" });

			// undo optimistic update
			this.dispatchReactionUpdate({
				operation: "add",
				timestamp: getUnixTime(new Date()),
				userId: this.user.id,
				userFirstName: this.user.firstName,
				blockKey,
				emojiName,
				emojiUuid: null,
				id: "",
			});
		}

		this.removeLock.release(blockKey, emojiKey);
	}

	destroy = () => {
		for (let key of this.reactionWidgetNodes.keys()) {
			this.removeAtKey(key);
		}
	};
}
